import React, { useState } from 'react';
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { BETA_FEEDBACK_URL } from '../constants/feedbackForm';
import { getCurrentVersions, formatVersion, CurrentVersionsResponse } from '../utils/legalApi';
import { LegalPdfViewer } from './LegalPdfViewer';
import { en } from '../translations/en';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [legalDocs, setLegalDocs] = useState<CurrentVersionsResponse | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenDialog = async () => {
    setShowPrivacyDialog(true);
    if (!legalDocs && !fetchError) {
      try {
        const data = await getCurrentVersions();
        setLegalDocs(data);
      } catch {
        setFetchError(true);
      }
    }
  };

  return (
    <>
      <Box
        component="footer"
        sx={{
          mt: "auto",
          py: 2,
          px: 2,
          backgroundColor: theme.palette.grey[100],
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: theme.palette.grey[900],
          }),
          borderTop: 1,
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {en.footer.copyright.replace('{year}', currentYear.toString())}{" "}
          <Link
            component="button"
            variant="body2"
            color="inherit"
            underline="hover"
            onClick={handleOpenDialog}
            sx={{ cursor: "pointer" }}
          >
            {en.footer.termsAndPrivacy}
          </Link>
          {" | "}
          <Link
            href={BETA_FEEDBACK_URL}
            variant="body2"
            color="inherit"
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
          >
            {en.footer.betaFeedback}
          </Link>
          {" "}
          <Typography
            component="span"
            variant="caption"
            color="text.disabled"
            sx={{ ml: 1 }}
          >
            v{__BUILD_SHA__}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
          {en.footer.copyrightExtended}
        </Typography>
      </Box>

      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenMobile}
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ mt: -1, flexGrow: 1 }}
          >
            <Tab label={en.footer.termsTab} />
            <Tab label={en.footer.privacyTab} />
          </Tabs>
          <IconButton onClick={() => setShowPrivacyDialog(false)} sx={{ ml: 1 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const docData = activeTab === 0 ? legalDocs?.tos : legalDocs?.privacy;
            if (fetchError) {
              return <Alert severity="error">Unable to load document. Please try again.</Alert>;
            }
            if (legalDocs && !docData) {
              return <Alert severity="info">Legal document not yet available. Please check back later.</Alert>;
            }
            return (
              <>
                {docData && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {en.legal.effective.replace('{date}', new Date(docData.effectiveDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }))}
                      {' \u2014 '}
                      {formatVersion(docData.version)}
                    </Typography>
                    <Button
                      component="a"
                      href={docData.presignedUrl}
                      download={docData.fileName}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="text"
                      startIcon={<FileDownloadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {en.legal.downloadPdf}
                    </Button>
                  </Box>
                )}
                {docData ? (
                  <LegalPdfViewer url={docData.presignedUrl} />
                ) : null}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrivacyDialog(false)}>{en.footer.close}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
